import { create } from 'zustand';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AppConfig } from '@/datasources/AppConfig';
import { Logo } from '@/types/media';

export interface CurrentOuvidoriaData {
  id: string;
  nome: string;
  cnpj: string;
  arquivada: boolean;
  geral: boolean;
  logo?: Logo | null;
  razaoSocial?: string | null;
  naturezaJuridica?: string | null;
  nomeFantasia?: string | null;
  email?: string | null;
  telefone?: string | null;
  etiquetas?: string[] | null;
  endereco?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    pais?: string;
  } | null;
  latitude?: number;
  longitude?: number;
  altitude?: number;
}

interface CurrentOuvidoriaState {
  ouvidoria: CurrentOuvidoriaData | null;
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  fetchCurrentOuvidoria: (force?: boolean) => Promise<void>;
}

const api = new RemoteDataSource(Settings.urlBase);

function unwrap(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null;
  if (Array.isArray(raw)) return (raw[0] as Record<string, unknown>) ?? null;
  const r = raw as Record<string, unknown>;
  if (Array.isArray(r.data) && (r.data as unknown[])[0]) return (r.data as Record<string, unknown>[])[0];
  if (r.data && typeof r.data === 'object') return r.data as Record<string, unknown>;
  if (Array.isArray(r.item) && (r.item as unknown[])[0]) return (r.item as Record<string, unknown>[])[0];
  if (r.ouvidoria && typeof r.ouvidoria === 'object') return r.ouvidoria as Record<string, unknown>;
  return r;
}

export const useCurrentOuvidoriaStore = create<CurrentOuvidoriaState>((set, get) => ({
  ouvidoria: null,
  isLoading: false,
  error: null,
  hasFetched: false,

  fetchCurrentOuvidoria: async (force = false) => {
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) return;
    if (!force && get().hasFetched) return;

    set({ isLoading: true, error: null });
    try {
      const result = await api.get<CurrentOuvidoriaData>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}`,
        headers: { Accept: 'application/vnd.data.full.v1+json' },
        forceRefresh: true,
        serialize: (raw) => (unwrap(raw) as unknown as CurrentOuvidoriaData) ?? ({} as CurrentOuvidoriaData),
      });

      if ('failure' in result) {
        set({
          isLoading: false,
          error: result.failure.messages?.join(', ') || 'Erro ao carregar ouvidoria',
          hasFetched: true,
        });
        return;
      }

      const data = result.success.data;
      if (!data || !data.id) {
        set({ ouvidoria: null, isLoading: false, hasFetched: true });
        return;
      }

      set({ ouvidoria: data, isLoading: false, hasFetched: true });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Erro ao carregar ouvidoria',
        hasFetched: true,
      });
    }
  },
}));
